import {
  AdminHeaderActionButton,
  AdminPageHeader,
  AdminPageShell,
} from '@/components/patterns/admin-page'
import ProductCatalogBuilder from '@/components/products/ProductCatalogBuilder'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { collectionQueries } from '@/lib/services/collections/collections.query'
import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { ChevronLeft, Printer } from 'lucide-react'
import { z } from 'zod/v4'

export const Route = createFileRoute('/_authenticated/$orgId/products/catalog-builder')({
  component: ProductCatalogBuilderPage,
  head: adminRouteTitleHead,
  validateSearch: z.object({
    collectionId: z.coerce.number().int().positive().optional().catch(undefined),
  }),
  loaderDeps: ({ search }) => ({ collectionId: search.collectionId }),
  loader: async ({ context, deps, params }) => {
    if (!context.ability.can('list', 'products')) throw notFound()

    const isGlobalOrganization =
      Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

    if (isGlobalOrganization) throw notFound()

    const canListCollections = context.ability.can('list', 'collections')

    if (!deps.collectionId) {
      return { canListCollections, initialCollections: [], isReadOnly: false }
    }

    if (!canListCollections) throw notFound()

    const collection = await context.queryClient.ensureQueryData(
      collectionQueries.show({
        organizationId: params.orgId,
        collectionId: deps.collectionId,
      })
    )

    return { canListCollections, initialCollections: [collection], isReadOnly: false }
  },
})

export function ProductCatalogBuilderPage() {
  const { orgId } = Route.useParams()
  const { canListCollections, initialCollections } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <AdminPageShell className="pb-0 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden">
      <AdminPageHeader
        icon={Printer}
        title="Catalog Builder"
        actions={
          <AdminHeaderActionButton variant="outline" asChild>
            <Link {...path.products.index().link}>
              <ChevronLeft />
              Back to products
            </Link>
          </AdminHeaderActionButton>
        }
      />

      <ProductCatalogBuilder
        organizationId={orgId}
        initialCollections={initialCollections}
        canListCollections={canListCollections}
      />
    </AdminPageShell>
  )
}
